import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Check } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { FormSection } from '../../components/ui/FormSection';
import { NicknameField } from '../../components/integrations/NicknameField';
import { resolveNickname } from '../../lib/mappingNickname';
import { TextField } from '../../components/ui/TextField';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Toast } from '../../components/ui/Toast';
import { UpgradePrompt } from '../../components/UpgradePrompt';
import { uid, type Mapping } from '../../types';
import { LimitError } from '../../api/dataApi';

// GitHub Actions fast path (roadmap #9 Phase 1 slice 5,
// docs/CLAUDE_ZMIST_INTEGRATION_TOOL_IMPLEMENTATION.md §7.4): name ->
// channel -> create -> webhook URL + secret -> wait for first delivery.
// No raw JSON shown - the Mapping this creates (sourceType "webhook",
// spec.provider "github") is the same shape `internal/webhookrecv/github.go`
// already turns into CI pass/fail + deploy Signals with zero further
// config (githubBuiltinRules), so there is nothing else for this flow to
// configure. Anyone who needs custom event handling still has the
// existing Advanced Mapping form (spec.rules/spec.outputs).
export function GitHubSetupScreen() {
  const navigate = useNavigate();
  const { channels, isChannelOwner, addMapping } = useStore();
  const ownedChannels = channels.filter(isChannelOwner);

  const [title, setTitle] = useState('');
  const [nicknameInput, setNicknameInput] = useState('');
  const [channelNickname, setChannelNickname] = useState(ownedChannels[0]?.nickname ?? '');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [limitMessage, setLimitMessage] = useState('');
  const [created, setCreated] = useState<Mapping | null>(null);
  const [secretCopied, setSecretCopied] = useState(false);

  const canCreate = title.trim() !== '' && channelNickname.trim() !== '' && !creating;

  const create = async () => {
    if (!canCreate) return;
    setCreating(true);
    setError('');
    try {
      const payload: Mapping = {
        id: uid(),
        nickname: resolveNickname(nicknameInput, '@github', uid().slice(0, 8)),
        title: title.trim(),
        description: `GitHub Actions -> #${channelNickname}`,
        isPublic: false,
        sourceType: 'webhook',
        spec: { provider: 'github', channel: channelNickname },
      };
      const saved = await addMapping(payload);
      setCreated(saved);
    } catch (err) {
      // {code:"connector_limit"} - a fresh webhook connector past the
      // caller's tier cap (backend/workflows/mapping/create.wsl).
      if (err instanceof LimitError) {
        setLimitMessage(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Unable to create this integration.');
      }
    } finally {
      setCreating(false);
    }
  };

  const copySecret = async () => {
    if (!created?.webhookSecret) return;
    try {
      await navigator.clipboard.writeText(created.webhookSecret);
      setSecretCopied(true);
    } catch {
      // Clipboard access denied/unavailable - still on screen to copy by hand.
    }
  };

  if (created) {
    return (
      <div className="flex flex-col gap-4">
        <ScreenHeader title="Connect GitHub webhook" backTo="/integrations/catalog" />

        <FormSection title="Add this webhook to your repository">
          <p className="text-xs text-black/45 dark:text-white/45 -mt-1">
            In GitHub: Settings → Webhooks → Add webhook. Paste the Payload URL and Secret below, set Content type to{' '}
            <code>application/json</code>, and choose "Send me everything" (or just Workflow runs + Deployment
            statuses - the only two event types this integration turns into Signals today).
          </p>

          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-black/70 dark:text-white/70">Payload URL</span>
            <code className="rounded-lg bg-black/[0.06] dark:bg-white/10 px-3 py-2 text-[13px] font-mono select-all break-all">
              {`POST /ingest/webhook/${created.nickname}`}
            </code>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-black/70 dark:text-white/70">Secret</span>
            <p className="text-xs text-black/45 dark:text-white/45">
              Unlike a one-time token, this stays visible on the integration's Settings tab later - GitHub's HMAC
              verification needs the real value, not a hash of it, so you can always come back for it if you need to
              re-paste it.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 min-w-0 truncate rounded-lg bg-black/[0.06] dark:bg-white/10 px-3 py-2 text-[13px] font-mono select-all">
                {created.webhookSecret}
              </code>
              <Button variant="secondary" onClick={copySecret}>
                {secretCopied ? <Check size={16} /> : <Copy size={16} />}
                {secretCopied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </div>
        </FormSection>

        <FormSection title="Waiting for first event">
          <p className="text-sm text-black/60 dark:text-white/60">
            There's no live delivery status yet (that's a later phase of this roadmap item) - so this won't update on
            its own. Trigger a workflow run or deployment in the repo, then check the destination channel for the
            Signal it should produce.
          </p>
          <Badge>Configured</Badge>
        </FormSection>

        <Button onClick={() => navigate(`/integrations/${created.id}`)} full>
          Go to integration
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <ScreenHeader title="Connect GitHub Actions" backTo="/integrations/catalog" />

      <FormSection title="Name this integration">
        <TextField
          label="Name"
          placeholder="GitHub Actions deploys"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <NicknameField value={nicknameInput} onChange={setNicknameInput} example="@deploys" />
      </FormSection>

      <FormSection title="Destination channel">
        {ownedChannels.length === 0 ? (
          <p className="text-sm text-black/60 dark:text-white/60">
            You need to own at least one channel before connecting an integration - create a channel first, then come
            back here.
          </p>
        ) : (
          <Select label="Channel" value={channelNickname} onChange={(e) => setChannelNickname(e.target.value)}>
            {ownedChannels.map((c) => (
              <option key={c.id} value={c.nickname}>
                {c.title} ({c.nickname})
              </option>
            ))}
          </Select>
        )}
      </FormSection>

      <FormSection title="What this creates">
        <p className="text-sm text-black/60 dark:text-white/60">
          Workflow runs (once completed) become a CI pass/fail Signal; deployment status changes become a deploy
          Signal. Every other GitHub event type is accepted but doesn't produce a Signal yet - there's no per-event
          filter to configure here because the backend doesn't have one today.
        </p>
      </FormSection>

      <Button onClick={create} loading={creating} disabled={!canCreate || ownedChannels.length === 0} full>
        Create integration
      </Button>

      {error && <Toast message={error} onDismiss={() => setError('')} />}
      <UpgradePrompt
        open={!!limitMessage}
        onClose={() => setLimitMessage('')}
        message={limitMessage}
        title="Connector limit reached"
      />
    </div>
  );
}

